import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { FiatWalletAccountState } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { CreateFiatWallet } from '@sorare/core/src/components/fiatWallet/CreateFiatWallet';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useCashWalletKycEvents } from '@sorare/core/src/hooks/wallets/useCashWalletKycEvents';
import { useFiatBalance } from '@sorare/core/src/hooks/wallets/useFiatBalance';
import { isCryptocurrency } from '@sorare/core/src/lib/cryptocurrency';
import { glossary } from '@sorare/core/src/lib/glossary';
import {
  EnterCashWalletKYCFlow_Source,
  EnterCashWalletKYCFlow_Target,
} from '@sorare/core/src/protos/events/platform/web/events';

import { AboveMarketWarning } from '#src/components/directOffer/NewOfferBuilder/AboveMarketWarning/index.tsx';
import { switchToBuilding } from '#src/components/directOffer/NewOfferBuilder/actions.ts';
import type {
  CardDataType,
  StateProps,
} from '#src/components/directOffer/NewOfferBuilder/types.ts';
import { OfferDealSummary } from '#src/components/offer/OfferDealSummary/index.tsx';
import { TokenTransferValidator } from '#src/components/token/TokenTransferValidator/index.tsx';
import { useStateOffer } from '#src/hooks/offers/useStateOffer.ts';

import type { OfferBuilderSummary_anyCard } from './__generated__/index.graphql.ts';

type BaseType = CardDataType & OfferBuilderSummary_anyCard;

type Props<D extends BaseType> = StateProps<D> & {
  cancel: () => void;
  title: string;
  sender: ReactNode;
  receiver: ReactNode;
};

const Content = styled.div`
  width: 100%;
  padding: 0 var(--triple-unit) var(--triple-unit) var(--triple-unit);
`;
const WarningBlockquote = styled.div`
  padding-bottom: var(--double-unit);
`;
const ButtonsBar = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: var(--double-unit);
`;
const CancelCta = styled(Button)`
  width: 50%;
`;
const ConfirmCta = styled(Button)`
  width: 50%;
`;
const FooterWrapper = styled(Vertical)`
  padding: var(--unit) var(--triple-unit) var(--triple-unit) var(--triple-unit);
  box-shadow: 0px 14px 50px rgba(0, 0, 0, 0.2);
  width: 100%;
`;

export const Summary = <D extends BaseType>({
  title,
  cancel,
  dispatch,
  state,
  sender,
  receiver,
}: Props<D>) => {
  const { trackEnter } = useCashWalletKycEvents();
  const [needsCreateFiatWallet, setNeedsCreateFiatWallet] = useState(false);
  const { canListAndTrade } = useFiatBalance();

  const { up: isTablet } = useScreenSize('tablet');

  const {
    sendAmount,
    receiveAmount,
    receiveMarketFeesAmount,
    sendAmountCurrency,
    receiveAmountCurrency,
    sendCards,
    cardsData,
    receiveCards,
    stage,
    duration,
    paymentMethod,
  } = state;

  const setBuilding = () => dispatch(switchToBuilding);

  const onConfirm = () => {
    setNeedsCreateFiatWallet(false);
    if (
      !canListAndTrade &&
      !isCryptocurrency(receiveAmountCurrency) &&
      receiveAmount.isPositive
    ) {
      setNeedsCreateFiatWallet(true);
      trackEnter({
        source: EnterCashWalletKYCFlow_Source.COUNTEROFFER_TRADE_OFFER,
        target: EnterCashWalletKYCFlow_Target.OWNER,
      });
      return;
    }
    state.submit(dispatch, state);
  };
  const [consentAgreed, setConsentAgreed] = useState(false);

  const receiveCardsWithToken = receiveCards.map(c => cardsData[c.slug]);

  const sendCardsWithToken = sendCards.map(c => cardsData[c.slug]);

  const { offerAboveMarketValue } = useStateOffer(state);

  const isSubmitting = stage === 'submitting';

  if (needsCreateFiatWallet) {
    return (
      <CreateFiatWallet
        statusTarget={FiatWalletAccountState.OWNER}
        onClose={() => setNeedsCreateFiatWallet(false)}
        onDismissActivationSuccess={onConfirm}
        canDismissAfterActivation
      />
    );
  }
  return (
    <TokenTransferValidator
      cards={sendCardsWithToken}
      transferContext="send_trade"
    >
      {({ validationMessages, ConsentMessage }) => (
        <Dialog
          open
          maxWidth="md"
          fullWidth
          onBack={setBuilding}
          fullScreen={!isTablet}
          title={<BodyM className="text-center">{title}</BodyM>}
          footer={
            <FooterWrapper>
              {!!ConsentMessage && (
                <ConsentMessage
                  value={consentAgreed}
                  onChange={setConsentAgreed}
                />
              )}
              <ButtonsBar>
                <CancelCta
                  color="tertiary"
                  onClick={cancel}
                  disabled={isSubmitting}
                  size="medium"
                >
                  <FormattedMessage {...glossary.cancel} />
                </CancelCta>
                <ConfirmCta
                  onClick={onConfirm}
                  size="medium"
                  color="primary"
                  loading={isSubmitting}
                  disabled={!!ConsentMessage && !consentAgreed}
                >
                  <FormattedMessage {...glossary.confirm} />
                </ConfirmCta>
              </ButtonsBar>
            </FooterWrapper>
          }
        >
          <Content>
            {!!offerAboveMarketValue && (
              <WarningBlockquote>
                <AboveMarketWarning />
              </WarningBlockquote>
            )}
            <OfferDealSummary
              sendAmount={sendAmount}
              receiveAmount={receiveAmount}
              sendAmountCurrency={sendAmountCurrency}
              receiveAmountCurrency={receiveAmountCurrency}
              marketFeeAmount={receiveMarketFeesAmount}
              receiveCards={receiveCardsWithToken}
              sendCards={sendCardsWithToken}
              duration={duration}
              withEmpty
              sender={sender}
              receiver={receiver}
              validationMessages={validationMessages}
              paymentMethod={paymentMethod}
            />
          </Content>
        </Dialog>
      )}
    </TokenTransferValidator>
  );
};

Summary.fragments = {
  anyCard: gql`
    fragment OfferBuilderSummary_anyCard on AnyCardInterface {
      slug
      assetId
      walletStatus
      tradeableStatus
      user {
        slug
      }
      ...OfferDealSummary_anyCard
      ...useStateOffer_anyCard
    }
    ${OfferDealSummary.fragments.anyCard}
    ${useStateOffer.fragments.anyCard}
  ` as TypedDocumentNode<OfferBuilderSummary_anyCard>,
};
